import { useState, useEffect, useCallback } from "react";
import { BarChart3, TrendingUp, Clock, Users, Flame } from "lucide-react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  if (i === 0) return "12a";
  if (i < 12) return `${i}a`;
  if (i === 12) return "12p";
  return `${i - 12}p`;
});

// Color scale: transparent → cyan → blue → purple
const heatColor = (val, max) => {
  if (!max || val === 0) return "rgba(255,255,255,0.02)";
  const t = Math.min(val / max, 1);
  if (t < 0.25) return `rgba(6, 182, 212, ${0.1 + t * 0.6})`;
  if (t < 0.5) return `rgba(59, 130, 246, ${0.25 + (t - 0.25) * 1.4})`;
  if (t < 0.75) return `rgba(139, 92, 246, ${0.5 + (t - 0.5) * 1.2})`;
  return `rgba(168, 85, 247, ${0.7 + (t - 0.75) * 1.2})`;
};

const GridHeatmap = ({ data }) => {
  if (!data) return null;
  const { grid, max, days } = data;

  return (
    <div className="overflow-x-auto" data-testid="listener-heatmap-grid">
      <table className="w-full border-collapse" style={{ minWidth: 600 }}>
        <thead>
          <tr>
            <th className="text-white/30 text-[10px] font-medium p-1 text-left w-10" />
            {HOURS.map((h, i) => (
              <th key={i} className="text-white/25 text-[9px] font-medium p-0.5 text-center">{i % 3 === 0 ? h : ""}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day, di) => (
            <tr key={day}>
              <td className="text-white/40 text-[11px] font-semibold pr-2 py-0.5">{day}</td>
              {grid[di].map((val, hi) => (
                <td key={hi} className="p-0.5">
                  <div
                    className="w-full aspect-square rounded-[3px] transition-colors cursor-default"
                    style={{ backgroundColor: heatColor(val, max), minWidth: 14, minHeight: 14 }}
                    title={`${day} ${HOURS[hi]}: ${val} avg listeners`}
                    data-testid={`heatmap-cell-${di}-${hi}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 justify-end">
        <span className="text-white/25 text-[10px]">Less</span>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <div key={i} className="w-3 h-3 rounded-sm" style={{ backgroundColor: heatColor(t * max, max) }} />
        ))}
        <span className="text-white/25 text-[10px]">More</span>
      </div>
    </div>
  );
};

const TrendChart = ({ data }) => {
  if (!data || !data.points.length) return null;
  const { points, peak_hour, peak_avg } = data;
  const maxVal = Math.max(...points.map(p => p.avg), 1);
  const chartW = 800;
  const chartH = 160;
  const padL = 30;
  const padR = 10;
  const padT = 10;
  const padB = 24;
  const w = chartW - padL - padR;
  const h = chartH - padT - padB;

  const pts = points.map((p, i) => ({
    x: padL + (i / (points.length - 1)) * w,
    y: padT + h - (p.avg / maxVal) * h,
    ...p,
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${pts[pts.length - 1].x} ${padT + h} L ${pts[0].x} ${padT + h} Z`;

  // Y axis labels
  const yTicks = [0, Math.round(maxVal / 2), Math.round(maxVal)];

  return (
    <div data-testid="listener-trend-chart">
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(139,92,246,0.3)" />
            <stop offset="100%" stopColor="rgba(139,92,246,0)" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {yTicks.map((v, i) => {
          const y = padT + h - (v / maxVal) * h;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="rgba(255,255,255,0.05)" strokeDasharray="4 4" />
              <text x={padL - 4} y={y + 3} textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="9">{v}</text>
            </g>
          );
        })}
        {/* Area fill */}
        <path d={areaPath} fill="url(#areaGrad)" />
        {/* Line */}
        <path d={linePath} fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* X axis labels — show every Nth */}
        {pts.filter((_, i) => i % Math.max(1, Math.floor(pts.length / 8)) === 0).map((p, i) => {
          const d = new Date(p.ts);
          const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return (
            <text key={i} x={p.x} y={chartH - 4} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="9">{label}</text>
          );
        })}
      </svg>
      {peak_hour !== null && (
        <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
          <Flame className="w-3.5 h-3.5 text-orange-400" />
          <span>Peak hour: <strong className="text-white/70">{peak_hour > 12 ? peak_hour - 12 : peak_hour || 12}:00 {peak_hour >= 12 ? "PM" : "AM"}</strong></span>
          <span className="text-white/20">|</span>
          <span>Avg: <strong className="text-white/70">{peak_avg}</strong> listeners</span>
        </div>
      )}
    </div>
  );
};

export const ListenerHeatmap = ({ authToken }) => {
  const [range, setRange] = useState("7d");
  const [heatmapData, setHeatmapData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("heatmap"); // heatmap | trend

  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${authToken}` };
    try {
      const [hRes, tRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/listener-heatmap?range=${range}`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/listener-trend?range=${range}`, { headers }),
      ]);
      if (hRes.ok) setHeatmapData(await hRes.json());
      if (tRes.ok) setTrendData(await tRes.json());
    } catch (e) {
      console.error("Listener analytics fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [authToken, range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalSamples = trendData?.total_samples || 0;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="listener-heatmap-section">
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" /> Listener Heatmap
            </h3>
            <p className="text-white/40 text-sm mt-1">
              Peak listening patterns across your stations
              {totalSamples > 0 && <span className="text-white/20 ml-2">({totalSamples.toLocaleString()} samples)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex bg-white/5 rounded-lg p-0.5">
              <button
                onClick={() => setActiveView("heatmap")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  activeView === "heatmap" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white/60"
                }`}
                data-testid="heatmap-view-toggle"
              >
                <BarChart3 className="w-3.5 h-3.5" /> Grid
              </button>
              <button
                onClick={() => setActiveView("trend")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  activeView === "trend" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white/60"
                }`}
                data-testid="trend-view-toggle"
              >
                <TrendingUp className="w-3.5 h-3.5" /> Trend
              </button>
            </div>
            {/* Time Range */}
            <div className="flex bg-white/5 rounded-lg p-0.5">
              {RANGES.map(r => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    range === r.key ? "bg-cyan-500/20 text-cyan-400" : "text-white/40 hover:text-white/60"
                  }`}
                  data-testid={`range-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-3 text-white/30">
            <Clock className="w-5 h-5 animate-pulse" />
            <span className="text-sm">Loading listener data...</span>
          </div>
        ) : totalSamples === 0 ? (
          <div className="text-center py-12">
            <Users className="w-10 h-10 mx-auto mb-3 text-white/10" />
            <p className="text-white/40 font-medium">No listener data yet</p>
            <p className="text-white/25 text-sm mt-1">The scanner captures listener counts every 2 minutes. Data will appear here soon.</p>
          </div>
        ) : (
          <>
            {activeView === "heatmap" && <GridHeatmap data={heatmapData} />}
            {activeView === "trend" && <TrendChart data={trendData} />}
          </>
        )}
      </div>
    </div>
  );
};
